/*
 * BFCM tier pricing - keeps tier badges, strikethrough prices and the cart drawer milestone bar in sync with the cart.
 * Config (thresholds, percentages, wording) comes from window.BFCM, set in snippets/bfcm-head.liquid.
 * Checkout discounts are NOT applied here - they come from Shopify automatic discounts.
 *
 * Tiers are measured on the cart value BEFORE discounts (cart.original_total_price), the same value
 * Shopify checks against the discounts' minimum purchase amounts.
 *
 * The bar only ever moves to a value confirmed by /cart.js. When the theme swaps in a new copy of the
 * cart drawer, the new bar is first drawn exactly as the old one looked, then animates to the confirmed value.
 *
 * Bar layout: milestones sit in equal columns (any order, free shipping, tier 1, tier 2). The fill starts at
 * the "any order" dot once the cart has something in it, moves dot to dot, and fills the open end after
 * the last dot only once every milestone is unlocked.
 */
(function () {
  'use strict';

  var C = window.BFCM;
  if (!C) return;

  var subtotal = null;        // last value confirmed by /cart.js (cents, before discounts)
  var refreshTimer = null;
  var requestSeq = 0;

  function pctFor(basis) {
    if (basis >= C.tier2) return C.pctTier2;
    if (basis >= C.tier1) return C.pctTier1;
    return C.pctBase;
  }

  function salePrice(price, pct) {
    return Math.round(price * (100 - pct) / 100);
  }

  function fillText(template, values) {
    return String(template).replace(/\[(\w+)\]/g, function (match, key) {
      return values[key] != null ? values[key] : match;
    });
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function formatMoney(cents, noDecimals) {
    var format = C.moneyFormat || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return (cents / 100).toFixed(noDecimals ? 0 : 2);

    function fmt(decimals, thousands, decimal) {
      if (noDecimals) decimals = 0;
      var parts = (cents / 100).toFixed(decimals).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmt(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(2, "'", '.'); break;
      case 'amount_with_space_separator': value = fmt(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(0, ' ', ','); break;
      default: value = fmt(2, ',', '.');
    }
    return format.replace(match[0], value);
  }

  /* ---------- Badges and strikethrough prices ---------- */

  function applyPrices() {
    if (subtotal === null) return;
    document.querySelectorAll('[data-bfcm-price]').forEach(function (el) {
      var price = parseInt(el.getAttribute('data-bfcm-price'), 10);
      if (isNaN(price)) return;
      var basis = el.getAttribute('data-bfcm-mode') === 'line' ? subtotal : Math.max(subtotal, price);
      var pct = pctFor(basis);

      if (el.hasAttribute('data-bfcm-badge')) {
        var badgeText = fillText(C.text.badge, { pct: pct });
        if (el.textContent !== badgeText) el.textContent = badgeText;
        return;
      }

      var sale = el.querySelector('.custom-sale-price');
      var regular = el.querySelector('.custom-regular-price');
      var saleText = formatMoney(salePrice(price, pct));
      var regularText = formatMoney(price);
      if (sale && sale.textContent.trim() !== saleText) sale.textContent = saleText;
      if (regular && regular.textContent.trim() !== regularText) regular.textContent = regularText;
    });
  }

  /* ---------- Product page variant changes ---------- */

  function syncVariants() {
    document.querySelectorAll('[data-bfcm-variants]').forEach(function (el) {
      var scope = el.closest('.shopify-section') || document;
      var input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      if (!input || !input.value) return;
      var pairs = el.getAttribute('data-bfcm-variants').split(',');
      for (var i = 0; i < pairs.length; i++) {
        var pair = pairs[i].split(':');
        if (pair[0] === String(input.value)) {
          if (el.getAttribute('data-bfcm-price') !== pair[1]) el.setAttribute('data-bfcm-price', pair[1]);
          break;
        }
      }
    });
  }

  /* ---------- Cart drawer milestone bar ---------- */

  /* Milestones after the start: free shipping (if enabled), tier 1, tier 2 - sorted by amount. */
  function getStops() {
    var stops = [];
    if (C.freeShipping) {
      stops.push({ amount: C.freeShipping, label: C.text.freeShippingLabel, name: C.text.freeShippingName });
    }
    var tier1Label = fillText(C.text.tier, { pct: C.pctTier1 });
    var tier2Label = fillText(C.text.tier, { pct: C.pctTier2 });
    stops.push({ amount: C.tier1, label: tier1Label, name: tier1Label });
    stops.push({ amount: C.tier2, label: tier2Label, name: tier2Label });
    return stops.sort(function (a, b) { return a.amount - b.amount; });
  }

  /* Shown under each milestone, rounded to whole units: 299.95 shows as 300. */
  function displayAmount(cents) {
    return formatMoney(Math.round(cents / 100) * 100, true);
  }

  function setFill(fillEl, width, animate) {
    if (!fillEl) return;
    if (animate) {
      fillEl.style.width = width;
      return;
    }
    fillEl.style.transition = 'none';
    fillEl.style.width = width;
    void fillEl.offsetWidth;
    fillEl.style.transition = '';
  }

  function renderProgress(el, sub, animate) {
    var stops = getStops();
    var nodes = [{ amount: 0, display: C.text.startLabel, label: fillText(C.text.tier, { pct: C.pctBase }), start: true }]
      .concat(stops.map(function (stop) {
        return { amount: stop.amount, display: displayAmount(stop.amount), label: stop.label };
      }));
    var slot = 100 / nodes.length;

    /* Fill: empty cart = 0; otherwise from the centre of the last reached dot towards the next one;
       everything unlocked = 100 (including the open end after the last dot). */
    var fill = 100;
    var nextIndex = -1;
    if (sub <= 0) {
      fill = 0;
      nextIndex = 0;
    } else {
      for (var i = 0; i < stops.length; i++) {
        if (sub < stops[i].amount) {
          var previous = i === 0 ? 0 : stops[i - 1].amount;
          fill = slot * (i + 0.5) + slot * (sub - previous) / (stops[i].amount - previous);
          nextIndex = i;
          break;
        }
      }
    }
    setFill(el.querySelector('[data-bfcm-progress-fill]'), Math.max(0, Math.min(100, fill)).toFixed(2) + '%', animate);

    var stopsEl = el.querySelector('[data-bfcm-progress-stops]');
    if (stopsEl) {
      stopsEl.style.setProperty('--bfcm-stops', nodes.length);
      if (stopsEl.getAttribute('aria-label') !== C.text.srMilestones) stopsEl.setAttribute('aria-label', C.text.srMilestones);
      var html = '';
      nodes.forEach(function (node) {
        var reached = node.start ? sub > 0 : sub >= node.amount;
        html += '<li class="bfcm-progress__stop' + (reached ? ' is-reached' : '') + '">' +
          '<span class="bfcm-progress__dot" aria-hidden="true"></span>' +
          '<span class="bfcm-progress__amount">' + escapeHtml(node.display) + '</span>' +
          ' <span class="bfcm-progress__label">' + escapeHtml(node.label) + '</span>' +
          '<span class="bfcm-visually-hidden">, ' + escapeHtml(reached ? C.text.srReached : C.text.srNotReached) + '</span>' +
          '</li>';
      });
      if (stopsEl.innerHTML !== html) stopsEl.innerHTML = html;
    }

    var textEl = el.querySelector('[data-bfcm-progress-text]');
    if (textEl) {
      var message;
      if (nextIndex === -1) {
        var all = fillText(C.text.tier, { pct: C.pctTier2 });
        if (C.freeShipping) all += ' + ' + C.text.freeShippingName;
        message = fillText(C.text.all, { all: all });
      } else {
        var lead = nextIndex === 0
          ? fillText(C.text.base, { pct: C.pctBase })
          : fillText(C.text.reached, { current: stops[nextIndex - 1].label });
        message = lead + ' ' + fillText(C.text.next, {
          amount: formatMoney(stops[nextIndex].amount - sub),
          next: stops[nextIndex].name
        });
      }
      /* Only touch the live region when the message really changes, so screen readers announce it once.
         The emoji is decorative and hidden from screen readers. */
      if (textEl.getAttribute('data-bfcm-message') !== message) {
        textEl.setAttribute('data-bfcm-message', message);
        textEl.innerHTML = escapeHtml(message).replace(/\ud83c\udf89\s*/g, '<span aria-hidden="true">\ud83c\udf89 </span>');
      }
    }
  }

  function applyProgress(animate) {
    if (subtotal === null) return;
    document.querySelectorAll('[data-bfcm-progress]').forEach(function (el) {
      renderProgress(el, subtotal, animate);
    });
  }

  /* ---------- Cart state ---------- */

  function refresh() {
    var seq = ++requestSeq;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return originalFetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (seq !== requestSeq) return; // a newer request is on its way
        subtotal = cart.original_total_price;
        syncVariants();
        applyPrices();
        applyProgress(true);
      })
      .catch(function (err) { console.error('BFCM tier pricing:', err); });
  }

  function scheduleRefresh(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, delay == null ? 50 : delay);
  }

  var originalFetch = window.fetch.bind(window);
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var request = originalFetch.apply(window, arguments);
    if (/\/cart\/(add|change|clear|update)/.test(url)) {
      request.then(function () { scheduleRefresh(0); }, function () {});
    }
    return request;
  };

  ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (name) {
    document.addEventListener(name, function () { scheduleRefresh(); });
  });

  document.addEventListener('change', function () {
    setTimeout(function () { syncVariants(); applyPrices(); }, 60);
  });

  /*
   * When the theme inserts a new copy of the drawer (or new product cards), draw it at the last confirmed
   * value straight away, without animation, so nothing jumps backwards. Then confirm with /cart.js.
   * The subtotal written into the inserted HTML is only used before the first /cart.js answer.
   */
  var observer = new MutationObserver(function (mutations) {
    var found = false;
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        var progressEls = node.matches('[data-bfcm-progress]') ? [node] : node.querySelectorAll('[data-bfcm-progress]');
        Array.prototype.forEach.call(progressEls, function (el) {
          var sub = subtotal;
          if (sub === null) sub = parseInt(el.getAttribute('data-bfcm-subtotal'), 10) || 0;
          renderProgress(el, sub, false);
          found = true;
        });
        if (node.matches('[data-bfcm-price]') || node.querySelector('[data-bfcm-price]')) found = true;
      });
    });
    if (found) scheduleRefresh(0);
  });

  function init() {
    document.querySelectorAll('[data-bfcm-progress]').forEach(function (el) {
      renderProgress(el, parseInt(el.getAttribute('data-bfcm-subtotal'), 10) || 0, false);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
